import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { GameHistory, GameStats } from '../types/gameHistory';

export function useGameHistory(walletAddress?: string) {
  const [history, setHistory] = useState<GameHistory[]>([]);
  const [stats, setStats] = useState<GameStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGameHistory();
    
    const channel = supabase
      .channel('game_history_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_history'
        },
        () => {
          fetchGameHistory();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [walletAddress]);

  const fetchGameHistory = async () => {
    try {
      let query = supabase
        .from('game_history')
        .select('*')
        .order('timestamp', { ascending: false });

      if (walletAddress) {
        query = query.eq('wallet_address', walletAddress);
      }

      const { data, error } = await query;
      if (error) throw error;

      const gameHistory = data.map(entry => ({
        id: entry.id,
        username: entry.username,
        walletAddress: entry.wallet_address,
        gameType: entry.game_type,
        betAmount: entry.bet_amount,
        result: entry.result,
        pointsChange: entry.points_change,
        timestamp: entry.timestamp
      }));

      setHistory(gameHistory);
      calculateStats(gameHistory);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch game history');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (games: GameHistory[]) => {
    const stats: GameStats = {
      totalGames: games.length,
      wins: games.filter(g => g.result === 'win').length,
      losses: games.filter(g => g.result === 'lose').length,
      draws: games.filter(g => g.result === 'draw').length,
      totalBet: games.reduce((sum, g) => sum + g.betAmount, 0),
      totalWon: games.filter(g => g.result === 'win')
        .reduce((sum, g) => sum + g.pointsChange, 0),
      totalLost: games.filter(g => g.result === 'lose')
        .reduce((sum, g) => sum + Math.abs(g.pointsChange), 0),
      netProfit: games.reduce((sum, g) => sum + g.pointsChange, 0)
    };
    setStats(stats);
  };

  const addGameResult = async (
    username: string,
    walletAddress: string,
    gameType: GameType,
    betAmount: number,
    result: 'win' | 'lose' | 'draw'
  ) => {
    const pointsChange = result === 'win' ? betAmount 
      : result === 'lose' ? -betAmount 
      : 0;

    try {
      const { error } = await supabase
        .from('game_history')
        .insert({
          username,
          wallet_address: walletAddress,
          game_type: gameType,
          bet_amount: betAmount,
          result,
          points_change: pointsChange,
          timestamp: new Date().toISOString()
        });

      if (error) throw error;
      await fetchGameHistory();
    } catch (err) {
      console.error('Failed to add game result:', err);
      throw err;
    }
  };

  return {
    history,
    stats,
    loading,
    error,
    addGameResult
  };
}